import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiBlob } from '../../lib/api.ts';
import { zEmail, zRuc, zTelefono } from '@postmaker/shared';
import { ModalImportar } from '../../componentes/Importar.tsx';
import { Download, Package, Plus, Search, Upload } from 'lucide-react';
import { Aviso, Avatar, Boton, Campo, Casilla, Chip, EncabezadoPagina, Insignia, Selector } from '../../componentes/ui.tsx';

export interface UsuarioCliente {
  id: string;
  username: string;
  email: string | null;
  rol: string;
  estado: string;
  idioma: string;
  ultimoAcceso: string | null;
}
export interface ClienteAdmin {
  id: string;
  codigoExterno: string;
  razonSocial: string;
  nombreNegocio: string | null;
  ruc: string;
  ciudad: string | null;
  region: string | null;
  email: string | null;
  telefono: string | null;
  categoriaId: string | null;
  categoriaNombre: string | null;
  tipoId: string | null;
  tipoNombre: string | null;
  contactoNombre: string | null;
  contactoTelefono: string | null;
  estado: string;
  numProductos: number;
  sinFoto: number;
  ultimoPedido: string | null;
  usuarios: UsuarioCliente[];
}
/** Prefijos de país para celulares de contacto; el último usado queda como default. */
export const PREFIJOS = [
  { codigo: '+593', pais: 'Ecuador' },
  { codigo: '+57', pais: 'Colombia' },
  { codigo: '+51', pais: 'Perú' },
  { codigo: '+52', pais: 'México' },
  { codigo: '+55', pais: 'Brasil' },
  { codigo: '+1', pais: 'EE.UU.' },
  { codigo: '+34', pais: 'España' },
];

export function partirPrefijo(tel: string | null): { prefijo: string; numero: string } {
  const defecto = localStorage.getItem('pm_prefijo_contacto') ?? '+593';
  if (!tel) return { prefijo: defecto, numero: '' };
  const limpio = tel.trim();
  const match = PREFIJOS.find((p) => limpio.startsWith(p.codigo));
  if (match) return { prefijo: match.codigo, numero: limpio.slice(match.codigo.length).trim() };
  return { prefijo: defecto, numero: limpio };
}

/** 09xxxxxxxx o +593 9xxxxxxxx → enlace wa.me. */
export function enlaceWhatsApp(tel: string): string {
  const limpio = tel.replace(/[^\d+]/g, '');
  if (limpio.startsWith('+')) return `https://wa.me/${limpio.slice(1)}`;
  const digitos = limpio.replace(/\D/g, '');
  return `https://wa.me/${digitos.startsWith('0') ? `593${digitos.slice(1)}` : digitos}`;
}

export interface Clasificacion {
  id: string;
  nombre: string;
  clientes: number;
}

interface Credencial {
  usuarioId: string;
  username: string;
  password: string;
}

export function PaginaAdminClientes() {
  const qc = useQueryClient();
  const navegar = useNavigate();
  const [q, setQ] = useState('');
  const [filtroEstado, setFiltroEstado] = useState<'todos' | 'ACTIVO' | 'INACTIVO'>('todos');
  const [filtroRegion, setFiltroRegion] = useState('');
  const [soloProblemas, setSoloProblemas] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [editando, setEditando] = useState<ClienteAdmin | null>(null);
  const [creando, setCreando] = useState(false);
  const [clasificando, setClasificando] = useState(false);
  const [credReset, setCredReset] = useState<CredencialReset | null>(null);
  const [modalUsuario, setModalUsuario] = useState<{ cliente: ClienteAdmin; usuario: UsuarioCliente | null } | null>(null);
  const [importando, setImportando] = useState(false);
  const [importandoPedidos, setImportandoPedidos] = useState(false);

  const clientes = useQuery({
    queryKey: ['admin-clientes', q],
    queryFn: () =>
      api<{ items: ClienteAdmin[] }>(`/admin/clientes?limit=100${q ? `&q=${encodeURIComponent(q)}` : ''}`),
  });

  const recargar = () => qc.invalidateQueries({ queryKey: ['admin-clientes'] });

  const todos = clientes.data?.items ?? [];
  const regiones = [...new Set(todos.map((c) => c.region).filter((r): r is string => Boolean(r)))].sort();
  const visibles = todos.filter((c) => {
    if (filtroEstado !== 'todos' && c.estado !== filtroEstado) return false;
    if (filtroRegion && c.region !== filtroRegion) return false;
    if (soloProblemas && !(c.numProductos > 0 && c.sinFoto > 0)) return false;
    return true;
  });
  // Cobertura de fotos: con menos del 60% con foto el almacén casi no puede trabajar.
  const cobertura = (c: ClienteAdmin) => (c.numProductos > 0 ? (c.numProductos - c.sinFoto) / c.numProductos : 1);
  const sinNinguna = todos.filter((c) => c.numProductos > 0 && c.sinFoto === c.numProductos);
  const coberturaBaja = todos.filter((c) => c.numProductos > 0 && c.sinFoto < c.numProductos && cobertura(c) < 0.6);

  // Mismo formato del importador: editar el archivo y re-importarlo actualiza los almacenes.
  async function descargarExcel() {
    const blob = await apiBlob('/admin/clientes.xlsx');
    if (!blob) {
      setAviso('Error: no se pudo generar el Excel.');
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'clientes.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  }

  const resetear = useMutation({
    mutationFn: ({ usuarioId }: { usuarioId: string; cliente: ClienteAdmin }) =>
      api<Credencial>(`/admin/usuarios/${usuarioId}/reset-password`, { metodo: 'POST' }),
    onSuccess: async (cred, { cliente }) => {
      setAviso(null);
      setCredReset({
        username: cred.username,
        password: cred.password,
        telefono: cliente.contactoTelefono ?? cliente.telefono,
        copiada: await copiarClave(cred.password),
      });
    },
    onError: (e) => setAviso(`Error: ${e.message}`),
  });

  const alternarUsuario = useMutation({
    mutationFn: (u: UsuarioCliente) =>
      api(`/admin/usuarios/${u.id}`, {
        metodo: 'PATCH',
        cuerpo: { estado: u.estado === 'ACTIVO' ? 'INACTIVO' : 'ACTIVO' },
      }),
    onSuccess: () => recargar(),
    onError: (e) => setAviso(`Error: ${e.message}`),
  });

  const eliminarUsuario = useMutation({
    mutationFn: (id: string) => api(`/admin/usuarios/${id}`, { metodo: 'DELETE' }),
    onSuccess: async () => {
      setAviso('Cuenta eliminada.');
      await recargar();
    },
    onError: (e) => setAviso(`Error: ${e.message}`),
  });

  async function entrarComoUsuario(u: UsuarioCliente) {
    try {
      await api(`/admin/usuarios/${u.id}/impersonar`, { metodo: 'POST' });
      window.location.href = '/';
    } catch (e) {
      setAviso(`Error: ${(e as Error).message}`);
    }
  }

  const alternarEstado = useMutation({
    mutationFn: (c: ClienteAdmin) =>
      api(`/admin/clientes/${c.id}`, {
        metodo: 'PATCH',
        cuerpo: { estado: c.estado === 'ACTIVO' ? 'INACTIVO' : 'ACTIVO' },
      }),
    onSuccess: () => recargar(),
  });

  const eliminar = useMutation({
    mutationFn: ({ id, confirmarRuc }: { id: string; confirmarRuc: string }) =>
      api(`/admin/clientes/${id}`, { metodo: 'DELETE', cuerpo: { confirmarRuc } }),
    onSuccess: async () => {
      setAviso('Cliente eliminado con sus usuarios, pedidos y precios.');
      await recargar();
    },
    onError: (e) => setAviso(`Error: ${e.message}`),
  });

  async function entrarEnCuenta(c: ClienteAdmin) {
    // El servidor cambia la cookie de sesión por la del almacén y deja retorno (+ auditoría).
    try {
      await api(`/admin/clientes/${c.id}/impersonar`, { metodo: 'POST' });
      window.location.href = '/';
    } catch (e) {
      setAviso(`Error: ${(e as Error).message}`);
    }
  }

  function borrar(c: ClienteAdmin) {
    const escrito = window.prompt(
      `Esto BORRA "${c.razonSocial}" con sus usuarios, pedidos y precios. No hay vuelta atrás.\n` +
        `Para suspender sin perder nada use Desactivar.\n\nEscriba el RUC exacto para confirmar: ${c.ruc}`,
    );
    if (escrito === null) return;
    eliminar.mutate({ id: c.id, confirmarRuc: escrito });
  }

  const accion =
    'inline-flex min-h-9 items-center rounded-lg bg-superficie2 px-2.5 text-xs font-semibold text-principal hover:bg-superficie2';

  return (
    <main className="mx-auto w-full max-w-[1600px] space-y-4">
      <EncabezadoPagina
        titulo="Clientes"
        subtitulo={`${clientes.data?.items.length ?? 0} almacenes`}
        acciones={
          <>
            <div className="relative">
              <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-suave" />
              <input
                placeholder="Buscar por razón social o RUC"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="campo con-icono w-64 bg-superficie shadow-[var(--sombra)]"
              />
            </div>
            <Boton variante="contorno" onClick={() => setCreando(true)}>
              <Plus size={16} /> Cliente
            </Boton>
            <Boton variante="contorno" onClick={() => setClasificando(true)}>
              Categorías y tipos
            </Boton>
            <Boton variante="contorno" onClick={() => setImportando(true)}>
              <Upload size={16} /> Importar
            </Boton>
            <Boton variante="contorno" onClick={() => void descargarExcel()}>
              <Download size={16} /> Excel
            </Boton>
            <Boton variante="contorno" onClick={() => setImportandoPedidos(true)}>
              <Package size={16} /> Pedidos
            </Boton>
          </>
        }
      />

      {aviso ? <Aviso tono={aviso.startsWith('Error') ? 'error' : 'ok'}>{aviso}</Aviso> : null}
      {credReset ? <AvisoCredencial cred={credReset} alCerrar={() => setCredReset(null)} /> : null}

      {sinNinguna.length > 0 ? (
        <Aviso>
          {sinNinguna.length === 1
            ? `${sinNinguna[0]!.nombreNegocio ?? sinNinguna[0]!.razonSocial} no tiene NINGUNA foto disponible de su pedido: no puede generar posts.`
            : `${sinNinguna.length} almacenes no tienen NINGUNA foto disponible de sus pedidos: ${sinNinguna.map((c) => c.nombreNegocio ?? c.razonSocial).join(', ')}.`}
          {' '}Use Buscar fotos en Productos.
        </Aviso>
      ) : null}
      {coberturaBaja.length > 0 ? (
        <Aviso>
          Menos del 60% de los productos con foto en:{' '}
          {coberturaBaja
            .map((c) => `${c.nombreNegocio ?? c.razonSocial} (${Math.round(cobertura(c) * 100)}%)`)
            .join(', ')}
          .
        </Aviso>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Chip activo={filtroEstado === 'todos'} onClick={() => setFiltroEstado('todos')}>Todos</Chip>
        <Chip activo={filtroEstado === 'ACTIVO'} onClick={() => setFiltroEstado('ACTIVO')}>Activos</Chip>
        <Chip activo={filtroEstado === 'INACTIVO'} onClick={() => setFiltroEstado('INACTIVO')}>Inactivos</Chip>
        <span className="mx-1 h-5 w-px bg-superficie2" />
        <Chip activo={soloProblemas} onClick={() => setSoloProblemas((v) => !v)}>Con fotos faltantes</Chip>
        {regiones.map((r) => (
          <Chip key={r} activo={filtroRegion === r} onClick={() => setFiltroRegion(filtroRegion === r ? '' : r)}>
            {r}
          </Chip>
        ))}
      </div>

      <ul className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {visibles.map((c) => (
          <li key={c.id} className="tarjeta p-5">
            <div className="flex flex-wrap items-start justify-between gap-2">
              {/* Cabecera tipo tarjeta de contacto (referencia): avatar + nombre + tags */}
              {/* Toda la cabecera es tocable: entra a la página del cliente. */}
              <div
                className="flex min-w-0 flex-1 cursor-pointer items-start gap-3"
                role="link"
                tabIndex={0}
                onClick={() => void navegar({ to: '/admin/clientes/$clienteId', params: { clienteId: c.id } })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void navegar({ to: '/admin/clientes/$clienteId', params: { clienteId: c.id } });
                }}
              >
                <Avatar nombre={c.nombreNegocio ?? c.razonSocial} />
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-medium">
                    {c.nombreNegocio ?? c.razonSocial}
                    {c.estado !== 'ACTIVO' ? <span className="ml-2"><Insignia tono="alerta">INACTIVO</Insignia></span> : null}
                  </p>
                  {c.nombreNegocio && c.nombreNegocio !== c.razonSocial ? (
                    <p className="text-xs text-suave">{c.razonSocial}</p>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Metadatos a lo ancho de la tarjeta, con título por columna (patrón de la ficha) */}
            <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1.5 sm:grid-cols-5">
                {(
                  [
                    ['RUC', c.ruc],
                    ['Ciudad', [c.ciudad, c.region].filter(Boolean).join(' · ')],
                    ['Productos', String(c.numProductos)],
                    ['Usuarios', String(c.usuarios.length)],
                    ['Último pedido', c.ultimoPedido ? new Date(c.ultimoPedido).toLocaleDateString() : 'sin pedidos'],
                    // Segunda fila fija: clasificación — tarjetas siempre parejas
                    ['Categoría', c.categoriaNombre ?? 'Sin categoría'],
                    ['Tipo', c.tipoNombre ?? 'Sin tipo'],
                  ] as const
                ).map(([etiqueta, valor]) => (
                  <div key={etiqueta}>
                    <p className="text-[10px] font-medium uppercase tracking-wide text-suave">{etiqueta}</p>
                    <p className="truncate text-xs">{valor || '—'}</p>
                  </div>
                ))}
                <div className="col-span-2 flex items-end sm:col-span-3">
                  {c.numProductos > 0 && c.sinFoto === c.numProductos ? (
                    <span className="tag bg-rose-500/10 text-rose-600">SIN FOTOS</span>
                  ) : c.sinFoto > 0 ? (
                    <span className="tag bg-amber-500/10 text-amber-600">{c.sinFoto} sin foto</span>
                  ) : null}
                </div>
            </div>

            {/* Acciones del cliente: entre los datos y las cuentas de acceso */}
            <div className="mt-3 flex flex-wrap gap-1.5">
              <button type="button" className={accion} onClick={() => void navegar({ to: '/admin/clientes/$clienteId', params: { clienteId: c.id } })}>Pedidos</button>
              <button type="button" className={accion} onClick={() => setEditando(c)}>Editar</button>
              <button type="button" className={accion} onClick={() => void entrarEnCuenta(c)}>Entrar</button>
              <button type="button" className={accion} onClick={() => setModalUsuario({ cliente: c, usuario: null })}>+ Usuario</button>
              <button type="button" className={accion} onClick={() => alternarEstado.mutate(c)}>
                {c.estado === 'ACTIVO' ? 'Desactivar' : 'Activar'}
              </button>
              <button
                type="button"
                className={`${accion} border-rose-200 text-rose-600 hover:bg-rose-50`}
                onClick={() => borrar(c)}
              >
                Eliminar
              </button>
            </div>

            <ul className="mt-3 space-y-1.5">
              {c.usuarios.map((u) => (
                <li key={u.id} className="interno flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-1.5">
                      <span className="font-mono text-[13px]">{u.username}</span>
                      <Insignia tono={u.rol === 'CLIENTE_ADMIN' ? 'negro' : 'gris'}>
                        {u.rol === 'CLIENTE_ADMIN' ? 'admin' : 'vendedor'}
                      </Insignia>
                      {u.estado !== 'ACTIVO' ? <Insignia tono="alerta">inactivo</Insignia> : null}
                    </p>
                    <p className="text-[11px] text-suave">
                      {u.email ?? 'sin email'} · {u.idioma}
                      {u.ultimoAcceso ? ` · último acceso ${u.ultimoAcceso.slice(0, 10)}` : ' · nunca entró'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <button type="button" className={accion} onClick={() => setModalUsuario({ cliente: c, usuario: u })}>Editar</button>
                    <button type="button" className={accion} onClick={() => void entrarComoUsuario(u)}>Entrar</button>
                    <button type="button" className={accion} onClick={() => resetear.mutate({ usuarioId: u.id, cliente: c })}>Clave nueva</button>
                    <button type="button" className={accion} onClick={() => alternarUsuario.mutate(u)}>
                      {u.estado === 'ACTIVO' ? 'Desactivar' : 'Activar'}
                    </button>
                    <button
                      type="button"
                      className={`${accion} border-rose-200 text-rose-600 hover:bg-rose-50`}
                      onClick={() => {
                        if (window.confirm(`¿Eliminar la cuenta ${u.username}? No hay vuelta atrás.`)) eliminarUsuario.mutate(u.id);
                      }}
                    >
                      Eliminar
                    </button>
                  </div>
                </li>
              ))}
              {c.usuarios.length === 0 ? <li className="text-xs text-suave">Sin cuentas de acceso.</li> : null}
            </ul>
          </li>
        ))}
        {clientes.data?.items.length === 0 ? (
          <li className="tarjeta px-4 py-6 text-sm text-suave">
            {todos.length === 0 ? 'Sin clientes. Cree uno con + Cliente o use Importar.' : 'Ningún cliente coincide con los filtros.'}
          </li>
        ) : null}
      </ul>

      {modalUsuario ? (
        <ModalUsuario
          cliente={modalUsuario.cliente}
          usuario={modalUsuario.usuario}
          alCerrar={() => setModalUsuario(null)}
          alListo={async (mensaje) => {
            setModalUsuario(null);
            if (mensaje) setAviso(mensaje);
            await recargar();
          }}
          alFallar={(m) => setAviso(`Error: ${m}`)}
        />
      ) : null}

      {clasificando ? (
        <ModalClasificaciones
          alCerrar={() => {
            setClasificando(false);
            void recargar();
          }}
        />
      ) : null}

      {editando || creando ? (
        <ModalCliente
          cliente={editando}
          alCerrar={() => {
            setEditando(null);
            setCreando(false);
          }}
          alListo={async (mensaje) => {
            setEditando(null);
            setCreando(false);
            if (mensaje) setAviso(mensaje);
            await recargar();
          }}
          alFallar={(m) => setAviso(`Error: ${m}`)}
        />
      ) : null}
      {importandoPedidos ? (
        <ModalImportar
          tipo="PEDIDOS"
          titulo="Subir pedidos"
          plantilla={{ ruta: '/admin/importaciones/plantillas/pedidos.xlsx', nombre: 'plantilla-pedidos.xlsx' }}
          columnas="Acepta el Excel real de pedidos: Pedido, CODIGO ORFA, LISTA DE PRECIOS A CON IVA, Descripción Color (actualiza el color del producto) y las columnas de tallas como cantidades — agregue una columna ruc_cliente o codigo_cliente para identificar al almacén, y opcionalmente fecha_de_facturacion (sin ella vale la fecha de la subida). También vale el formato simple: codigo, ruc_cliente, fecha, referencia, cantidad, costo_unitario. Define qué ve cada almacén y a qué costo (gana la compra más reciente)."
          alCerrar={() => setImportandoPedidos(false)}
          alAplicado={async () => {
            setImportandoPedidos(false);
            await recargar();
          }}
        />
      ) : null}

      {importando ? (
        <ModalImportar
          tipo="CLIENTES"
          titulo="Importar clientes"
          columnas="Acepta el Excel de la empresa tal cual (Identification Number, Nombre, ID de la ciudad/Nombre, Código de Cliente, Móvil, Correo electrónico) o las columnas ruc, razon_social, ciudad, codigo, telefono, email, estado. Los clientes nuevos reciben usuario y clave generada. Puede partir del Excel exportado con el botón Excel."
          alCerrar={() => setImportando(false)}
          alAplicado={async () => {
            setImportando(false);
            await recargar();
          }}
        />
      ) : null}

    </main>
  );
}

export function ModalCliente({
  cliente,
  alCerrar,
  alListo,
  alFallar,
}: {
  cliente: ClienteAdmin | null;
  alCerrar: () => void;
  alListo: (mensaje?: string) => Promise<void>;
  alFallar: (mensaje: string) => void;
}) {
  const [razonSocial, setRazonSocial] = useState(cliente?.razonSocial ?? '');
  const [nombreNegocio, setNombreNegocio] = useState(cliente?.nombreNegocio ?? '');
  const [ruc, setRuc] = useState(cliente?.ruc ?? '');
  const [ciudad, setCiudad] = useState(cliente?.ciudad ?? '');
  const [region, setRegion] = useState(cliente?.region ?? '');
  const [email, setEmail] = useState(cliente?.email ?? '');
  const [telefono, setTelefono] = useState(cliente?.telefono ?? '');
  const [categoriaId, setCategoriaId] = useState(cliente?.categoriaId ?? '');
  const [tipoId, setTipoId] = useState(cliente?.tipoId ?? '');
  const [contactoNombre, setContactoNombre] = useState(cliente?.contactoNombre ?? '');
  const contactoInicial = partirPrefijo(cliente?.contactoTelefono ?? null);
  const [prefijoContacto, setPrefijoContacto] = useState(contactoInicial.prefijo);
  const [contactoTelefono, setContactoTelefono] = useState(contactoInicial.numero);
  const categorias = useQuery({
    queryKey: ['cliente-categorias'],
    queryFn: () => api<{ items: Clasificacion[] }>('/admin/cliente-categorias'),
  });
  const tipos = useQuery({
    queryKey: ['cliente-tipos'],
    queryFn: () => api<{ items: Clasificacion[] }>('/admin/cliente-tipos'),
  });
  const [guardando, setGuardando] = useState(false);
  const [tocados, setTocados] = useState<Record<string, boolean>>({});

  // Se valida en vivo (al escribir/salir del campo), no recién al enviar.
  const primerError = (v: string, schema: { safeParse: (x: string) => { success: boolean; error?: { issues: { message: string }[] } } }) => {
    const r = schema.safeParse(v);
    return r.success ? undefined : r.error?.issues[0]?.message;
  };
  const errores = {
    ruc: ruc.trim() ? primerError(ruc.trim(), zRuc) : cliente ? undefined : 'El RUC es obligatorio',
    email: email.trim() ? primerError(email.trim(), zEmail) : undefined,
    telefono: telefono.trim() ? primerError(telefono.trim(), zTelefono) : undefined,
  };
  const hayErrores = Boolean(errores.ruc ?? errores.email ?? errores.telefono);
  const tocar = (campo: string) => setTocados((t) => ({ ...t, [campo]: true }));
  const visible = (campo: keyof typeof errores) => (tocados[campo] ? errores[campo] : undefined);

  async function guardar(evento: React.FormEvent) {
    evento.preventDefault();
    if (hayErrores) {
      setTocados({ ruc: true, email: true, telefono: true });
      return;
    }
    setGuardando(true);
    if (contactoTelefono.trim()) localStorage.setItem('pm_prefijo_contacto', prefijoContacto);
    try {
      if (cliente) {
        await api(`/admin/clientes/${cliente.id}`, {
          metodo: 'PATCH',
          cuerpo: {
            razonSocial: razonSocial.trim(),
            nombreNegocio: nombreNegocio.trim() || undefined,
            ciudad: ciudad.trim() || undefined,
            region: region || undefined,
            categoriaId: categoriaId || null,
            tipoId: tipoId || null,
            contactoNombre: contactoNombre.trim() || null,
            contactoTelefono: contactoTelefono.trim() ? `${prefijoContacto} ${contactoTelefono.trim().replace(/^0+/, '')}` : null,
            email: email.trim() || undefined,
            telefono: telefono.trim() || undefined,
          },
        });
        await alListo();
      } else {
        const r = await api<{ credencial: Credencial }>('/admin/clientes', {
          metodo: 'POST',
          cuerpo: {
            codigoExterno: ruc.trim(),
            razonSocial: razonSocial.trim(),
            nombreNegocio: nombreNegocio.trim() || undefined,
            ruc: ruc.trim(),
            ciudad: ciudad.trim() || undefined,
            region: region || undefined,
            categoriaId: categoriaId || null,
            tipoId: tipoId || null,
            contactoNombre: contactoNombre.trim() || null,
            contactoTelefono: contactoTelefono.trim() ? `${prefijoContacto} ${contactoTelefono.trim().replace(/^0+/, '')}` : null,
            email: email.trim() || undefined,
            telefono: telefono.trim() || undefined,
          },
        });
        await alListo(
          `Cliente creado. Usuario ${r.credencial.username} · clave ${r.credencial.password} — anótela ahora.`,
        );
      }
    } catch (e) {
      alFallar((e as Error).message);
      alCerrar();
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
      <form onSubmit={guardar} noValidate className="tarjeta w-full max-w-lg space-y-3 p-6 shadow-[var(--sombra-alta)]">
        <h2 className="text-[22px] font-light">{cliente ? `Editar ${cliente.ruc}` : 'Nuevo cliente'}</h2>
        <Campo etiqueta="Razón social" value={razonSocial} onChange={(e) => setRazonSocial(e.target.value)} required />
        <div className="grid grid-cols-2 gap-3">
          <Campo etiqueta="Nombre del negocio" value={nombreNegocio} onChange={(e) => setNombreNegocio(e.target.value)} />
          <Campo
            etiqueta="RUC (usuario de acceso)"
            value={ruc}
            onChange={(e) => { setRuc(e.target.value); tocar('ruc'); }}
            onBlur={() => tocar('ruc')}
            required
            disabled={cliente !== null}
            inputMode="numeric"
            error={visible('ruc')}
          />
          <Campo etiqueta="Ciudad" value={ciudad} onChange={(e) => setCiudad(e.target.value)} />
          <Selector etiqueta="Región" value={region} onChange={(e) => setRegion(e.target.value)}>
            <option value="">Sin región</option>
            <option value="Costa">Costa</option>
            <option value="Sierra">Sierra</option>
            <option value="Oriente">Oriente</option>
            <option value="Galápagos">Galápagos</option>
          </Selector>
          <Selector etiqueta="Categoría" value={categoriaId} onChange={(e) => setCategoriaId(e.target.value)}>
            <option value="">Sin categoría</option>
            {categorias.data?.items.map((x) => (
              <option key={x.id} value={x.id}>{x.nombre}</option>
            ))}
          </Selector>
          <Selector etiqueta="Tipo de cliente" value={tipoId} onChange={(e) => setTipoId(e.target.value)}>
            <option value="">Sin tipo</option>
            {tipos.data?.items.map((x) => (
              <option key={x.id} value={x.id}>{x.nombre}</option>
            ))}
          </Selector>
          <Campo
            etiqueta="Persona de contacto"
            value={contactoNombre}
            onChange={(e) => setContactoNombre(e.target.value)}
          />
          <div>
            <p className="mb-1.5 text-[13px] text-suave">Celular del contacto</p>
            <div className="flex gap-2">
              <Selector
                aria-label="Prefijo de país"
                className="min-h-11 w-28"
                value={prefijoContacto}
                onChange={(e) => setPrefijoContacto(e.target.value)}
              >
                {PREFIJOS.map((pf) => (
                  <option key={pf.codigo} value={pf.codigo}>{pf.codigo}</option>
                ))}
              </Selector>
              <input
                className="campo min-h-11 flex-1"
                value={contactoTelefono}
                onChange={(e) => setContactoTelefono(e.target.value)}
                inputMode="tel"
                placeholder="988888888"
              />
            </div>
          </div>
          <Campo
            etiqueta="Email"
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); tocar('email'); }}
            onBlur={() => tocar('email')}
            error={visible('email')}
          />
          <Campo
            etiqueta="Teléfono"
            value={telefono}
            onChange={(e) => { setTelefono(e.target.value); tocar('telefono'); }}
            onBlur={() => tocar('telefono')}
            inputMode="tel"
            error={visible('telefono')}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Boton type="button" variante="secundario" onClick={alCerrar}>Cancelar</Boton>
          <Boton type="submit" disabled={guardando || hayErrores || !razonSocial.trim()}>{guardando ? 'Guardando…' : 'Guardar'}</Boton>
        </div>
      </form>
    </div>
  );
}


/** Alta/edición de una cuenta de acceso del almacén (usuario, rol, idioma, clave). */
export function ModalUsuario({
  cliente,
  usuario,
  alCerrar,
  alListo,
  alFallar,
}: {
  cliente: ClienteAdmin;
  usuario: UsuarioCliente | null;
  alCerrar: () => void;
  alListo: (mensaje?: string) => Promise<void>;
  alFallar: (mensaje: string) => void;
}) {
  const [username, setUsername] = useState(usuario?.username ?? '');
  const [email, setEmail] = useState(usuario?.email ?? '');
  const [rol, setRol] = useState(usuario?.rol ?? 'CLIENTE_VENDEDOR');
  const [idioma, setIdioma] = useState(usuario?.idioma ?? 'ES');
  const [clave, setClave] = useState('');
  const [forzarCambio, setForzarCambio] = useState(usuario === null);
  const [guardando, setGuardando] = useState(false);

  async function guardar(evento: React.FormEvent) {
    evento.preventDefault();
    setGuardando(true);
    try {
      if (usuario) {
        await api(`/admin/usuarios/${usuario.id}`, {
          metodo: 'PATCH',
          cuerpo: { username: username.trim(), email: email.trim() || null, rol, idioma },
        });
        if (clave) {
          await api(`/admin/usuarios/${usuario.id}/password`, {
            metodo: 'POST',
            cuerpo: { password: clave, debeCambiarPassword: forzarCambio },
          });
        }
        await alListo(clave ? `Cuenta ${username} actualizada con clave nueva.` : undefined);
      } else {
        const r = await api<Credencial>(`/admin/clientes/${cliente.id}/usuarios`, {
          metodo: 'POST',
          cuerpo: {
            username: username.trim(),
            email: email.trim() || undefined,
            rol,
            idioma,
            ...(clave ? { password: clave } : {}),
            debeCambiarPassword: forzarCambio,
          },
        });
        await alListo(`Cuenta ${r.username} creada · clave ${r.password} — anótela ahora.`);
      }
    } catch (e) {
      alFallar((e as Error).message);
      alCerrar();
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
      <form onSubmit={guardar} className="tarjeta w-full max-w-lg space-y-3 p-6 shadow-[var(--sombra-alta)]">
        <h2 className="text-[22px] font-light">
          {usuario ? 'Editar cuenta' : 'Nueva cuenta'}{' '}
          <span className="text-suave">· {cliente.nombreNegocio ?? cliente.razonSocial}</span>
        </h2>
        <div className="grid grid-cols-2 gap-3">
          <Campo etiqueta="Usuario (para entrar)" value={username} onChange={(e) => setUsername(e.target.value)} required />
          <Campo etiqueta="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Selector etiqueta="Rol" value={rol} onChange={(e) => setRol(e.target.value)}>
            <option value="CLIENTE_ADMIN">Admin del almacén (ve costos)</option>
            <option value="CLIENTE_VENDEDOR">Vendedor (sin costos)</option>
          </Selector>
          <Selector etiqueta="Idioma" value={idioma} onChange={(e) => setIdioma(e.target.value)}>
            <option value="ES">Español</option>
            <option value="PT_BR">Português</option>
          </Selector>
          <Campo
            etiqueta={usuario ? 'Clave nueva (vacío = no cambia)' : 'Clave (vacío = se genera)'}
            value={clave}
            onChange={(e) => setClave(e.target.value)}
            autoComplete="new-password"
          />
          <div className="flex items-end pb-1">
            <Casilla activa={forzarCambio} onCambiar={setForzarCambio} etiqueta="Debe cambiarla al entrar" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Boton type="button" variante="secundario" onClick={alCerrar}>Cancelar</Boton>
          <Boton type="submit" disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar'}</Boton>
        </div>
      </form>
    </div>
  );
}

/** Gestor de categorías y tipos de cliente: un tab por clase, a lo ancho. */
function ModalClasificaciones({ alCerrar }: { alCerrar: () => void }) {
  const [tab, setTab] = useState<'categorias' | 'tipos'>('categorias');
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Categorías y tipos de cliente"
      onClick={alCerrar}
    >
      <div
        className="tarjeta max-h-[88vh] w-full max-w-xl space-y-4 overflow-y-auto p-6 shadow-[var(--sombra-alta)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-[22px] font-light">Clasificación de clientes</h2>
          <Boton variante="secundario" tamano="sm" onClick={alCerrar}>Cerrar</Boton>
        </div>
        <div className="flex gap-2">
          <Chip activo={tab === 'categorias'} onClick={() => setTab('categorias')}>Categorías</Chip>
          <Chip activo={tab === 'tipos'} onClick={() => setTab('tipos')}>Tipos de cliente</Chip>
        </div>
        {tab === 'categorias' ? (
          <PanelClasificacion titulo="Categorías" base="cliente-categorias" sinNombre="Sin categoría" />
        ) : (
          <PanelClasificacion titulo="Tipos de cliente" base="cliente-tipos" sinNombre="Sin tipo" />
        )}
      </div>
    </div>
  );
}

function PanelClasificacion({
  titulo,
  base,
  sinNombre,
}: {
  titulo: string;
  base: 'cliente-categorias' | 'cliente-tipos';
  sinNombre: string;
}) {
  const qc = useQueryClient();
  const clave = [base];
  const lista = useQuery({
    queryKey: clave,
    queryFn: () => api<{ items: Clasificacion[] }>(`/admin/${base}`),
  });
  const [nueva, setNueva] = useState('');
  const [editando, setEditando] = useState<string | null>(null);
  const [nombreEdit, setNombreEdit] = useState('');
  /** id en proceso de borrado: se pregunta a cuál mover los clientes. */
  const [borrando, setBorrando] = useState<Clasificacion | null>(null);
  const [reemplazoId, setReemplazoId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const refrescar = () => qc.invalidateQueries({ queryKey: clave });

  const crear = useMutation({
    mutationFn: () => api(`/admin/${base}`, { metodo: 'POST', cuerpo: { nombre: nueva.trim() } }),
    onSuccess: () => {
      setNueva('');
      setError(null);
      void refrescar();
    },
    onError: (e) => setError(e.message),
  });
  const renombrar = useMutation({
    mutationFn: ({ id, nombre }: { id: string; nombre: string }) =>
      api(`/admin/${base}/${id}`, { metodo: 'PATCH', cuerpo: { nombre } }),
    onSuccess: () => {
      setEditando(null);
      setError(null);
      void refrescar();
    },
    onError: (e) => setError(e.message),
  });
  const borrar = useMutation({
    mutationFn: ({ id, reemplazo }: { id: string; reemplazo: string }) =>
      api(`/admin/${base}/${id}${reemplazo ? `?reemplazoId=${reemplazo}` : ''}`, { metodo: 'DELETE' }),
    onSuccess: () => {
      setBorrando(null);
      setReemplazoId('');
      setError(null);
      void refrescar();
    },
    onError: (e) => setError(e.message),
  });

  return (
    <div className="interno space-y-3 p-4" aria-label={titulo}>
      {error ? <Aviso>{error}</Aviso> : null}

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (nueva.trim()) crear.mutate();
        }}
      >
        <input
          className="campo min-h-10 flex-1 text-sm"
          placeholder="Nueva…"
          value={nueva}
          onChange={(e) => setNueva(e.target.value)}
        />
        <Boton type="submit" tamano="sm" disabled={crear.isPending || !nueva.trim()}>Crear</Boton>
      </form>

      <ul className="divide-y divide-superficie2 text-sm">
        {lista.data?.items.map((x) => (
          <li key={x.id} className="space-y-2 py-2">
            {editando === x.id ? (
              <form
                className="flex items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (nombreEdit.trim()) renombrar.mutate({ id: x.id, nombre: nombreEdit.trim() });
                }}
              >
                <input
                  className="campo min-h-9 flex-1 text-sm"
                  value={nombreEdit}
                  onChange={(e) => setNombreEdit(e.target.value)}
                  autoFocus
                />
                <Boton type="submit" tamano="sm">Guardar</Boton>
                <Boton variante="texto" tamano="sm" onClick={() => setEditando(null)}>Cancelar</Boton>
              </form>
            ) : (
              <div className="flex items-center justify-between gap-2">
                <span>
                  {x.nombre} <span className="text-xs text-suave">· {x.clientes} cliente(s)</span>
                </span>
                <span className="flex gap-1">
                  <Boton
                    variante="texto"
                    tamano="sm"
                    onClick={() => {
                      setEditando(x.id);
                      setNombreEdit(x.nombre);
                      setBorrando(null);
                    }}
                  >
                    Renombrar
                  </Boton>
                  <Boton
                    variante="texto"
                    tamano="sm"
                    onClick={() => {
                      setBorrando(x);
                      setReemplazoId('');
                      setEditando(null);
                    }}
                  >
                    Borrar
                  </Boton>
                </span>
              </div>
            )}

            {borrando?.id === x.id ? (
              <div className="rounded-xl bg-superficie2 p-3 text-xs">
                <p className="mb-2">
                  ¿Borrar «{x.nombre}»? Sus {x.clientes} cliente(s) pasan a:
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Selector
                    className="min-h-10 w-full min-w-48 flex-1 text-sm"
                    value={reemplazoId}
                    onChange={(e) => setReemplazoId(e.target.value)}
                  >
                    <option value="">{sinNombre}</option>
                    {lista.data?.items
                      .filter((o) => o.id !== x.id)
                      .map((o) => (
                        <option key={o.id} value={o.id}>{o.nombre}</option>
                      ))}
                  </Selector>
                  <Boton
                    variante="peligro"
                    tamano="sm"
                    disabled={borrar.isPending}
                    onClick={() => borrar.mutate({ id: x.id, reemplazo: reemplazoId })}
                  >
                    Borrar
                  </Boton>
                  <Boton variante="texto" tamano="sm" onClick={() => setBorrando(null)}>Cancelar</Boton>
                </div>
              </div>
            ) : null}
          </li>
        ))}
        {lista.data?.items.length === 0 ? (
          <li className="py-3 text-xs text-suave">Ninguna todavía.</li>
        ) : null}
      </ul>
    </div>
  );
}

/** Credencial recién generada: clave copiada al portapapeles + envío por WhatsApp. */
export interface CredencialReset {
  username: string;
  password: string;
  /** Teléfono destino: contacto del cliente, o el del cliente; null = pedir número. */
  telefono: string | null;
  copiada: boolean;
}

/** Copia la clave al portapapeles; devuelve si lo logró (http o permisos pueden negarlo). */
export async function copiarClave(password: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(password);
    return true;
  } catch {
    // Fallback para contextos sin la API async (o sin permiso): selección + execCommand.
    try {
      const area = document.createElement('textarea');
      area.value = password;
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      const ok = document.execCommand('copy');
      area.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

export function AvisoCredencial({ cred, alCerrar }: { cred: CredencialReset; alCerrar: () => void }) {
  const [prefijo, setPrefijo] = useState(localStorage.getItem('pm_prefijo_contacto') ?? '+593');
  const [numero, setNumero] = useState('');

  const mensaje = `${window.location.origin}\n\n*Nueva clave:* ${cred.password}\n*Usuario:* ${cred.username}`;

  function enviar(telefono: string) {
    window.open(`${enlaceWhatsApp(telefono)}?text=${encodeURIComponent(mensaje)}`, '_blank', 'noopener');
  }

  return (
    <Aviso tono="ok">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span>
          Clave de <span className="font-mono">{cred.username}</span>:{' '}
          <span className="font-mono font-semibold">{cred.password}</span>
          {cred.copiada ? ' — copiada al portapapeles.' : ' — anótela ahora.'}
        </span>
        {cred.telefono ? (
          <button
            type="button"
            className="inline-flex min-h-9 items-center rounded-full bg-emerald-500/15 px-4 text-sm font-semibold text-emerald-600"
            onClick={() => enviar(cred.telefono!)}
          >
            Enviar por WhatsApp
          </button>
        ) : (
          <form
            className="flex items-center gap-1.5"
            onSubmit={(e) => {
              e.preventDefault();
              if (!numero.trim()) return;
              localStorage.setItem('pm_prefijo_contacto', prefijo);
              enviar(`${prefijo} ${numero.trim().replace(/^0+/, '')}`);
            }}
          >
            <span className="text-xs text-suave">Sin teléfono del cliente:</span>
            <Selector
              aria-label="Prefijo de país"
              className="min-h-9 w-24 text-sm"
              value={prefijo}
              onChange={(e) => setPrefijo(e.target.value)}
            >
              {PREFIJOS.map((pf) => (
                <option key={pf.codigo} value={pf.codigo}>{pf.codigo}</option>
              ))}
            </Selector>
            <input
              className="campo min-h-9 w-36 text-sm"
              placeholder="988888888"
              inputMode="tel"
              value={numero}
              onChange={(e) => setNumero(e.target.value)}
            />
            <button
              type="submit"
              className="inline-flex min-h-9 items-center rounded-full bg-emerald-500/15 px-3 text-sm font-semibold text-emerald-600"
            >
              WhatsApp
            </button>
          </form>
        )}
        <button type="button" className="ml-auto text-sm text-suave underline" onClick={alCerrar}>
          Cerrar
        </button>
      </div>
    </Aviso>
  );
}
